/* eslint-disable react/sort-prop-types */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ActionList,
  Badge,
  Button,
  Card,
  Heading,
  Icon,
  Popover,
  TextStyle,
} from '@shopify/polaris';
import { HorizontalDotsMinor } from '@shopify/polaris-icons';
import { useTenantContext } from 'src/components/TenantProvider';
import styleConstants from 'src/styles/constants';

import { STATUS, BADGE_TYPE, BADGE_LABEL } from './constants';

const CourseCard = ({
  imageUrl,
  instructor,
  name,
  publishedStatus,
  shopifyProductId,
  thinkificProductId,
  thinkificCourseId,
  listProduct,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    session,
    tenant,
    queryParams: { shop },
  } = useTenantContext();
  const getCourseUrl = () => {
    const { REACT_APP_THINKIFIC_ENDPOINT } = process.env;
    const courseUrl = `https://${tenant.subdomain}.${REACT_APP_THINKIFIC_ENDPOINT}/manage/courses/${thinkificCourseId}?referrer=shopify`;
    return courseUrl;
  };
  // Before the shopifyProductId was just the ID, but in graph it now returns a GID
  // https://shopify.dev/docs/api/usage/gids#finding-equivalent-ids-between-rest-and-graphql
  const legacyShopifyProductId = shopifyProductId?.replace(
    'gid://shopify/Product/',
    ''
  );
  const shopifyProductUrl = `https://${shop}/admin/products/${legacyShopifyProductId}`;
  const handleClick = () => setIsOpen(!isOpen);
  const activator = (
    <Button
      accessibilityLabel={
        shopifyProductId ? `course actions for ${name}` : 'course actions'
      }
      icon={<Icon source={HorizontalDotsMinor} />}
      plain
      onClick={handleClick}
    />
  );

  let badgeType;
  let badgeLabel;

  switch (publishedStatus) {
    case STATUS.PUBLISHED:
      badgeType = BADGE_TYPE.SUCCESS;
      badgeLabel = BADGE_LABEL.PUBLISHED;
      break;
    case STATUS.DRAFT:
      badgeType = BADGE_TYPE.NEW;
      badgeLabel = BADGE_LABEL.DRAFT;
      break;
    case STATUS.PENDING:
      badgeType = BADGE_TYPE.ATTENTION;
      badgeLabel = BADGE_LABEL.PENDING;
      break;
    case STATUS.PRESELL:
      badgeType = BADGE_TYPE.ATTENTION;
      badgeLabel = BADGE_LABEL.PRESELL;
      break;
    default:
      break;
  }

  const items = shopifyProductId
    ? [
        {
          content: 'Edit on Thinkific',
          onAction: () => {
            window.open(getCourseUrl(), '_top');
          },
        },
        {
          content: 'Edit on Shopify',
          onAction: () => {
            window.open(shopifyProductUrl, '_top');
          },
        },
      ]
    : [
        {
          content: 'List on Shopify products',
          onAction: () => {
            listProduct({
              variables: {
                sessionId: session.id,
                thinkificProductId,
              },
            });
          },
        },
      ];

  return (
    <Card>
      <a
        className="Polaris-Link Polaris-Link--monochrome"
        href={getCourseUrl()}
        target="_top"
      >
        <div className="course-card__image" />
        <div className="course-card__body">
          <div className="course-card__body__heading">
            <Heading>{name}</Heading>
          </div>
          <TextStyle variation="subdued">{instructor}</TextStyle>
        </div>
      </a>
      <div className="course-card__footer">
        <div>
          <Badge status={badgeType}>{badgeLabel}</Badge>
          {!shopifyProductId && (
            <Badge status={BADGE_TYPE.WARNING}>{BADGE_LABEL.UNLISTED}</Badge>
          )}
        </div>
        <Popover activator={activator} active={isOpen} onClose={handleClick}>
          <ActionList items={items} />
        </Popover>
      </div>
      <style jsx>{`
        .course-card__image {
          height: 150px;
          background-image: url(${imageUrl});
          background-position: center;
          background-size: cover;
        }

        .course-card__body {
          display: flex;
          flex-flow: column;
          justify-content: space-between;
          min-height: 140px;
          padding: ${styleConstants.spacing.five} ${styleConstants.spacing.five}
            ${styleConstants.spacing.four};
        }

        .course-card__body {
          width: 100%;
          max-height: 2.4em;
          margin-right: -1em;
          padding-right: 1em;
          position: relative;
          line-height: 1.2em;
          text-align: justify;
          overflow: hidden;
        }

        .card__body__heading {
          width: 100%;
          max-height: 2.4em;
          margin-right: -1em;
          padding-right: 1em;
          position: relative;
          line-height: 1.2em;
          text-align: justify;
          overflow: hidden;
        }

        .card__body__heading:before {
          color: black;
          content: '...';
          position: absolute;
          right: 0;
          bottom: 0;
        }

        .card__body__heading:after {
          content: '';
          position: absolute;
          right: 0;
          width: 1em;
          height: 1em;
          margin-top: 0.2em;
          background: white;
        }

        .card__body__heading :global(.Polaris-Heading) {
          display: inline;
          line-height: 1.2em;
        }

        .course-card__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 48px;
          background-color: ${styleConstants.color.lightGrey};
          padding: 0 ${styleConstants.spacing.five};
        }
        .course-card__footer :global(.Polaris-Button--iconOnly) {
          margin: -0.4rem -1.2rem -0.8rem -0.8rem;
        }
      `}</style>
    </Card>
  );
};

CourseCard.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  instructor: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  publishedStatus: PropTypes.string.isRequired,
  shopifyProductId: PropTypes.string,
  thinkificProductId: PropTypes.string.isRequired,
  thinkificCourseId: PropTypes.string.isRequired,
  listProduct: PropTypes.func,
};

CourseCard.defaultProps = {
  shopifyProductId: null,
  listProduct: () => {},
};

export default CourseCard;
